import type { GetDialogAudienceRequest } from '@/entities/UserDialog/model/types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ApiDialogActionType } from '@/core/generated/api';
import { useCityFilterOptions } from '@/entities/City/model/useCityFilterOptions';
import { CityDropdown } from '@/shared/ui/CityDropdown/CityDropdown';
import { getDataTableFilterValue } from '@/shared/lib/dataTableFilters';
import { PersonSearchFilter } from '@/shared/ui/PersonSearchFilter/PersonSearchFilter';
import { dialogService } from '@/entities/UserDialog/api/dialogService';
import { useUserDialogStore } from '@/entities/UserDialog/model/store';
import { dialogBroadcastSchema, type DialogBroadcastFormValues } from '@/entities/UserDialog/model/schema';
import { createDialogBroadcastDefaults, dialogAudienceToRequest, dialogBroadcastToRequest } from '@/entities/UserDialog/mappers';
import { DIALOG_AUDIENCE_OPTIONS } from '@/entities/UserDialog/config/dialog.config';
import { createRHFFields } from '@/shared/ui/RHFFields/RHFFields';
import {
    EntityForm,
    EntityFormFields,
    EntityFormLayout,
    EntityFormStack,
} from '@/shared/ui/EntityFormLayout/EntityFormLayout';
import { BackButton } from '@/shared/ui/BackButton/BackButton';
import buttonClasses from '@/styles/buttons.module.scss';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Checkbox } from '@/shared/ui/Checkbox/Checkbox';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { RequestErrorBanner } from '@/shared/ui/RequestState';
import { RequestStatus } from '@/shared/model/requestStatus';
import styles from './UserDialogs.module.scss';

const Fields = createRHFFields<DialogBroadcastFormValues>('dialog-broadcast');

/** Позволяет выбрать получателей и выпустить диалоги */
export const DialogBroadcastFormPage = () => {
    const { templates, audience, loadTemplates, loadAudience, clearAudience } = useUserDialogStore();
    const navigate = useNavigate();
    const { cities, loading: citiesLoading } = useCityFilterOptions();

    const form = useForm<DialogBroadcastFormValues>({
        resolver: zodResolver(dialogBroadcastSchema),
        defaultValues: createDialogBroadcastDefaults()
    });

    const [applied, setApplied] = useState<GetDialogAudienceRequest | null>(null);
    const [first, setFirst] = useState(0);
    const [selected, setSelected] = useState<string[]>([]);
    const [allMatching, setAllMatching] = useState(false);

    const command = useRef({
        fingerprint: '',
        id: ''
    });

    const values = form.watch();
    const filterKey = JSON.stringify(dialogAudienceToRequest(values));

    const appliedKey = applied
        ? JSON.stringify({
            ...applied,
            skipCount: 0
        })
        : null;

    const previewCurrent = filterKey === appliedKey && audience.status === RequestStatus.Success;

    useEffect(() => {
        void loadTemplates();
        clearAudience();

        return clearAudience;
    }, [loadTemplates, clearAudience]);

    useEffect(() => {
        if (applied) void loadAudience({
            ...applied,
            skipCount: first
        });
    }, [applied, first, loadAudience]);

    const count = allMatching
        ? audience.data?.allCount ?? 0
        : selected.length;

    /** Обновляет аудиторию по текущим условиям */
    const preview = async () => {
        if (!await form.trigger('phoneSearch')) return;
        setSelected([]);
        setAllMatching(false);
        setFirst(0);
        setApplied(dialogAudienceToRequest(form.getValues()));
    };

    /** Выпускает диалоги проверенным получателям */
    const publish = form.handleSubmit(async current => {
        if (!previewCurrent || count === 0) return;
        const payload = dialogBroadcastToRequest(current, '', allMatching, selected);
        const fingerprint = JSON.stringify(payload);

        if (command.current.fingerprint !== fingerprint) command.current = {
            fingerprint,
            id: crypto.randomUUID()
        };
        try {
            const id = await dialogService.publish({
                ...payload,
                requestId: command.current.id
            });

            toast.success('Выпуск создан');
            navigate(`/user-dialogs/broadcasts/${id}`);
        } catch { /* Ошибка показана общим обработчиком API */ }
    });

    return <EntityFormLayout title="Новый выпуск диалогов">
        <EntityFormStack>
            <div className={buttonClasses.buttonGroup}>
                <BackButton
                    url="/user-dialogs"
                    title="К списку"
                />
            </div>
            <RequestErrorBanner
                {...templates}
                fallbackMessage="Не удалось загрузить шаблоны"
                onRetry={loadTemplates}
            />
            <FormProvider {...form}>
                <EntityForm onSubmit={publish}>
                    <EntityFormFields>
                        <Fields.TextField
                            name="name"
                            label="Название выпуска"
                            maxLength={150}
                        />
                        <Fields.SelectField
                            name="templateId"
                            label="Шаблон"
                            options={(templates.data ?? []).filter(item => item.isEnabled && (item.action === ApiDialogActionType.Acknowledge || item.action === ApiDialogActionType.RecordAnswer || item.action === ApiDialogActionType.CollectFeedback))
                                .map(item => ({
                                    id: item.id ?? '',
                                    name: item.title ?? ''
                                }))}
                        />
                        <Fields.DateField
                            name="activeFrom"
                            label="Показать начиная с (необязательно)"
                            showTime
                        />
                        <Fields.DateField
                            name="expiresAt"
                            label="Доступен до (необязательно)"
                            showTime
                        />
                    </EntityFormFields>
                    <h2>Аудитория</h2>
                    <EntityFormFields>
                        <Fields.SelectField
                            name="audience"
                            label="Приложения"
                            options={DIALOG_AUDIENCE_OPTIONS}
                        />
                        <Controller
                            name="cityId"
                            control={form.control}
                            render={({ field }) => <div className={styles.filterField}>
                                <span>Город (необязательно)</span>
                                <CityDropdown
                                    options={cities}
                                    loading={citiesLoading}
                                    className={styles.cityFilter}
                                    filters={{
                                        cityId: {
                                            value: field.value,
                                            matchMode: 'equals'
                                        }
                                    }}
                                    onFilterChange={({ filters }) => field.onChange(getDataTableFilterValue(filters, 'cityId') ?? null)}
                                />
                            </div>}
                        />
                        <PersonSearchFilter
                            name="dialog-audience-person"
                            label="Поиск получателя"
                            namePlaceholder="Введите имя получателя"
                            nameValue={values.nameSearch}
                            phoneValue={values.phoneSearch}
                            error={form.formState.errors.phoneSearch?.message}
                            onChange={(mode, value) => {
                                form.setValue('nameSearch', mode === 'name'
                                    ? value
                                    : '', { shouldDirty: true });

                                form.setValue('phoneSearch', mode === 'phone'
                                    ? value
                                    : '', {
                                    shouldDirty: true,
                                    shouldValidate: true
                                });
                            }}
                        />
                        <Fields.TextField
                            name="petNameSearch"
                            label="Кличка питомца (только клиенты)"
                        />
                    </EntityFormFields>
                    <ActionButton
                        type="button"
                        label="Найти получателей"
                        onClick={preview}
                        loading={audience.status === RequestStatus.Loading}
                    />
                    <RequestErrorBanner
                        {...audience}
                        fallbackMessage="Не удалось загрузить аудиторию"
                        onRetry={() => applied
                            ? loadAudience({
                                ...applied,
                                skipCount: first
                            })
                            : undefined}
                    />
                    {applied && !previewCurrent && audience.status !== RequestStatus.Loading &&
                        <p>Условия изменились. Найдите получателей заново.</p>
                    }
                    {previewCurrent &&
                        <div className={styles.selection}>
                            <label className={styles.selectAll}>
                                <Checkbox
                                    aria-label="Выбрать всех найденных"
                                    checked={allMatching}
                                    disabled={form.formState.isSubmitting}
                                    onCheckedChange={checked => {
                                        setAllMatching(checked);
                                        setSelected([]);
                                    }}
                                />
                                <span>Выбрать всех найденных</span>
                            </label>
                            <p>Всего найдено: {audience.data?.allCount ?? 0}. Выбор включает получателей на всех страницах.</p>
                            <p>Выбрано получателей: {count}. Для каждого будет создано одно диалоговое окно.</p>
                        </div>
                    }
                    <DataTable
                        data={previewCurrent
                            ? (audience.data?.items ?? []).map(item => ({
                                ...item,
                                selected: allMatching || selected.includes(item.id ?? '')
                            }))
                            : []
                        }
                        columns={[
                            {
                                field: 'selected',
                                header: 'Выбор',
                                body: item => <Checkbox
                                    aria-label={`Выбрать ${item.name}`}
                                    checked={item.selected}
                                    disabled={allMatching || form.formState.isSubmitting}
                                    onCheckedChange={checked => {
                                        if (item.id) setSelected(previous => checked
                                            ? [...previous, item.id ?? '']
                                            : previous.filter(id => id !== item.id));
                                    }}
                                />
                            },
                            {
                                field: 'name',
                                header: 'Получатель'
                            },
                            {
                                field: 'phone',
                                header: 'Телефон'
                            },
                            {
                                field: 'audience',
                                header: 'Приложение',
                                body: item => DIALOG_AUDIENCE_OPTIONS.find(option => option.id === item.audience)?.name
                            },
                            {
                                field: 'cityName',
                                header: 'Город'
                            },
                        ]}
                        paginator
                        lazy
                        rows={10}
                        first={first}
                        totalRecords={audience.data?.allCount ?? 0}
                        rowsPerPageOptions={[10]}
                        onPageChange={event => setFirst(event.first)}
                        loading={audience.status === RequestStatus.Loading}
                    />
                    <ActionButton
                        type="submit"
                        permission="admin.dialogs.publish"
                        label={`Выпустить для ${count} получателей`}
                        disabled={!previewCurrent || count === 0}
                        loading={form.formState.isSubmitting}
                    />
                </EntityForm>
            </FormProvider>
        </EntityFormStack>
    </EntityFormLayout>;
};
